import { useState } from 'react'
import { ChevronDown, ChevronUp, BookOpen } from 'lucide-react'
import { cn } from '@/components/ui/cn'

interface LessonContextPassageProps {
  passage: string
  defaultExpanded?: boolean
  /** Compact disclosure: collapsed by default peek, less chrome when open. */
  compact?: boolean
  className?: string
}

export function LessonContextPassage({
  passage,
  defaultExpanded = true,
  compact = false,
  className,
}: LessonContextPassageProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const peek = passage.replace(/\s+/g, ' ').trim()

  return (
    <div
      className={cn(
        compact ? 'rounded-lg border border-border/80 bg-muted/30' : 'rounded-xl border bg-card',
        className,
      )}
      data-testid="lesson-context-passage"
    >
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className={cn(
          'flex w-full items-start gap-2 text-left',
          compact ? 'px-3 py-2.5' : 'items-center justify-between gap-3 px-4 py-3',
        )}
        aria-expanded={expanded}
      >
        <BookOpen className={cn('mt-0.5 shrink-0 text-primary', compact ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
        <span className="min-w-0 flex-1">
          <span className={cn('font-semibold text-foreground', compact ? 'text-xs' : 'text-sm')}>
            {compact ? 'Scenario' : 'Read the scenario'}
          </span>
          {!expanded && compact ? (
            <span className="mt-0.5 line-clamp-2 block text-xs leading-snug text-muted-foreground">
              {peek}
            </span>
          ) : null}
        </span>
        {expanded ? (
          <ChevronUp className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </button>
      {expanded ? (
        <div className={cn('border-t', compact ? 'px-3 py-2.5' : 'px-4 py-3')}>
          <p
            className={cn(
              'whitespace-pre-wrap leading-relaxed text-foreground',
              compact ? 'text-sm' : 'text-sm',
            )}
          >
            {passage}
          </p>
          {!compact ? (
            <p className="mt-2 text-xs text-muted-foreground">All exercises are based on this text.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
